import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { type PropsWithChildren } from 'react';

type PanelProviderProps = PropsWithChildren;

interface IntermediateTab {
  component: string;
  tab: number;
  title: string;
}

interface PanelContextType {
  displayPanel: (arg?: boolean) => void;
  intermediateTab: IntermediateTab | null;
  isPanelDisplayed: boolean;
  resetPanelTab: () => void;
  selectedPanelTab: string;
  setIntermediateTab: (tab: IntermediateTab) => void;
  setSelectedPanelTab: (tab: string) => void;
}

const PanelContext = createContext<PanelContextType>({
  displayPanel: () => {},
  resetPanelTab: () => {},
  intermediateTab: null,
  isPanelDisplayed: false,
  selectedPanelTab: '',
  setIntermediateTab: () => {},
  setSelectedPanelTab: () => {},
});

export const PANEL_TABS = [
  {
    id: 'structure',
    label: __('Sections'),
  },
  {
    id: 'components',
    label: __('Components'),
  },
];

export const PanelProvider = ({ children }: PanelProviderProps) => {
  const [isPanelDisplayed, setIsPanelDisplayed] = useState(false);
  const [intermediateTab, setIntermediateTab] =
    useState<IntermediateTab | null>(null);
  const [selectedPanelTab, setSelectedPanelTab] = useState(PANEL_TABS[1].id);

  const displayPanel = (arg = true) => setIsPanelDisplayed(arg);
  const handleResetPanelTab = useCallback(() => {
    setIntermediateTab(null);
    setSelectedPanelTab('components');
  }, [setIntermediateTab, setSelectedPanelTab]);

  const value = useMemo(
    () => ({
      displayPanel,
      resetPanelTab: handleResetPanelTab,
      intermediateTab,
      isPanelDisplayed,
      selectedPanelTab,
      setIntermediateTab,
      setSelectedPanelTab,
    }),
    [
      handleResetPanelTab,
      setSelectedPanelTab,
      intermediateTab,
      isPanelDisplayed,
      selectedPanelTab,
      setIntermediateTab,
    ],
  );

  return (
    <PanelContext.Provider value={value}>{children}</PanelContext.Provider>
  );
};

export const usePanelContext = () => useContext(PanelContext);
